/**
 * Spec label/value formatting and category grouping — shared by the public
 * `SpecificationsViewer` (demos page) and the admin product-detail
 * Specifications tab, so both categorize and format the same way.
 */

export interface SpecCategory {
  name: string;
  icon: string;
  specs: string[];
  keywords: string[];
}

export const SPEC_CATEGORIES: SpecCategory[] = [
  {
    name: 'Image Sensor',
    icon: '📷',
    specs: [
      'sensorType',
      'numberOfPixelsEffective',
      'numberOfPixelsTotal',
      'aspectRatio',
      '14bitRAW',
      'colorSpace',
    ],
    keywords: ['sensor', 'pixel', 'megapixel', 'cmos', 'exmor', 'bsi', 'image size', 'effective pixel', 'total pixel', 'aspect ratio', 'color space', 'bit raw', 'image processor', 'bionz'],
  },
  {
    name: 'Lens & Focus',
    icon: '🔍',
    specs: [
      'lensMount',
      'focusType',
      'focusMode',
      'focusArea',
      'focusPoint',
      'focusSensitivityRange',
      'focusSensor',
      'aFIlluminator',
      'aFIlluminatorRange',
      'eYEAF',
      'focusMagnifier',
    ],
    keywords: ['lens', 'focus', 'mount', 'autofocus', 'af ', 'eye af', 'face detect', 'tracking', 'focus point', 'focus area', 'manual focus', 'focus mode', 'focus magnifier', 'lens compatibility'],
  },
  {
    name: 'Exposure & Metering',
    icon: '☀️',
    specs: [
      'exposureModes',
      'exposureCompensation',
      'exposureBracketing',
      'aELock',
      'meteringMode',
      'meteringType',
      'meteringSensor',
      'meteringSensitivity',
      'iSOSensitivityRecommendedExposureIndex',
      'shutterSpeed',
      'shutterType',
      'electronicFrontCurtainShutter',
      'flashSyncSpeed',
    ],
    keywords: ['exposure', 'metering', 'iso', 'shutter', 'aperture', 'ae lock', 'bracketing', 'compensation', 'electronic front curtain', 'flash sync', 'bulb'],
  },
  {
    name: 'Image Quality & Processing',
    icon: '🎨',
    specs: [
      'imageQualityModes',
      'imageSizePixels11',
      'imageSizePixels169',
      'imageSizePixels32',
      'imageSizePixels43',
      'dynamicRangeFunctions',
      'creativeStyle',
      'pictureEffect',
      'pictureProfile',
      'creativeLook',
      'noiseReduction',
      'setting',
    ],
    keywords: ['image quality', 'jpeg', 'raw', 'heif', 'dynamic range', 'hdr', 'picture profile', 'creative style', 'creative look', 'noise reduction', 'color mode', 'white balance', 'recording format', 'file format'],
  },
  {
    name: 'Video Recording',
    icon: '🎥',
    specs: [
      'maximumMovieResolutionAndFrameRates',
      'movieRecordingSystemXAVCHS4K',
      'movieRecordingSystemXAVCHS8K',
      'movieRecordingSystemXAVCS4K',
      'movieRecordingSystemXAVCSHD',
      'movieRecordingSystemXAVCSI4K',
      'movieRecordingSystemXAVCSIHD',
      'videoCompression',
      'audioRecordingFormat',
      'movieFunctions',
      'slowQuickMotionShootingFrameRate',
    ],
    keywords: ['video', 'movie', '4k', '8k', '1080', 'fps', 'frame rate', 'xavc', 'codec', 'recording movie', 'slow motion', 'time code', 'hdmi output', 'log', 's-log', 'hlg', 'audio recording', 'microphone'],
  },
  {
    name: 'Performance & Speed',
    icon: '⚡',
    specs: [
      'maximumPhotoResolutionAndFrameRates',
      'continuousDriveSpeedApproxMax',
      'dRIVEMODES',
      'noOfRecordableFramesApprox',
      'pixelShiftMultiShooting',
      'silentShooting',
      'antiFlickerShoot',
    ],
    keywords: ['continuous', 'burst', 'drive mode', 'fps shooting', 'recordable frames', 'buffer', 'silent shooting', 'anti-flicker', 'pixel shift', 'multi shot', 'high speed'],
  },
  {
    name: 'Display & Viewfinder',
    icon: '📺',
    specs: [
      'monitorType',
      'numberOfDots',
      'touchPanel',
      'adjustableAngle',
      'brightnessControl',
      'viewfinderType',
      'eyePoint',
      'fieldCoverage',
      'magnification',
      'finderFrameRateSelection',
      'displayFunction',
    ],
    keywords: ['monitor', 'lcd', 'screen', 'display', 'viewfinder', 'evf', 'oled', 'touch', 'tilt', 'vari-angle', 'articulating', 'dots', 'eye point', 'diopter', 'brightness', 'finder'],
  },
  {
    name: 'Flash & Lighting',
    icon: '💡',
    specs: [
      'flashModes',
      'flashCoverage',
      'guideNo',
      'recyclingTime',
      'flashCompensation',
      'fELevelLock',
      'externalFlashCompatibility',
      'wirelessControl',
    ],
    keywords: ['flash', 'strobe', 'speedlight', 'hot shoe', 'guide number', 'ttl', 'wireless flash', 'commander', 'pre-flash'],
  },
  {
    name: 'Storage & Media',
    icon: '💾',
    specs: [
      'media',
      'memoryCardSlot',
      'recordingModeOn2MemoryCards',
    ],
    keywords: ['memory card', 'sd card', 'cfexpress', 'card slot', 'dual slot', 'media', 'storage', 'uhs-ii', 'recording media'],
  },
  {
    name: 'Connectivity',
    icon: '📡',
    specs: [
      'wirelessLANBuiltIn',
      'bluetooth',
      'lANTerminal',
      'multiInterfaceShoe',
      'headphoneTerminal',
      'micTerminal',
      'multiMicroUSBTerminal',
      'syncTerminal',
      'verticalGripConnector',
      'pCInterface',
      'locationInformationLinkFromSmartphone',
    ],
    keywords: ['wifi', 'wireless', 'bluetooth', 'nfc', 'usb', 'hdmi', 'terminal', 'interface', 'tethering', 'remote', 'gps', 'location', 'smartphone', 'app', 'pc remote', 'ethernet', 'lan'],
  },
  {
    name: 'Power & Battery',
    icon: '🔋',
    specs: [
      'suppliedBattery',
      'batteryLifeStillImages',
      'batteryLifeMovieActualRecording',
      'batteryLifeMovieContinuousRecording',
      'internalBatteryCharge',
      'uSBPowerSupply',
      'powerConsumptionWithLCDScreen',
      'powerConsumptionWithViewfinder',
    ],
    keywords: ['battery', 'power', 'charge', 'usb power', 'ac adapter', 'shots per charge', 'battery life', 'lithium', 'rechargeable'],
  },
  {
    name: 'Physical & Design',
    icon: '📐',
    specs: [
      'dimensionsWXHXD',
      'weightWithBatteryAndMemoryCardIncluded',
      'diopterAdjustment',
      'antiDustSystem',
    ],
    keywords: ['dimension', 'weight', 'size', 'body', 'weather seal', 'dust', 'moisture', 'magnesium', 'grip', 'operating temperature'],
  },
  {
    name: 'Features & Functions',
    icon: '⚙️',
    specs: [
      'whiteBalanceModes',
      'sHUTTERAWBLOCK',
      'compensationEffect',
      'clearImageZoom',
      'digitalZoom',
      'faceDetection',
      'others',
      'playMemoriesCameraApps',
      'selfPortraitSelfTimer',
      'compatibleStandards',
      'operatingTemperature',
      'microphone',
      'speaker',
      'functions',
      'modes',
      'photoCapture',
    ],
    keywords: ['self timer', 'interval', 'timer', 'bracketing', 'panorama', 'hdr', 'stabilization', 'ibis', 'ois', 'steady shot', 'face detection', 'smile shutter', 'scene', 'mode', 'function', 'digital zoom', 'clear image zoom'],
  },
  {
    name: 'Audio & Sound',
    icon: '🎙️',
    specs: [],
    keywords: ['audio', 'microphone', 'mic', 'speaker', 'sound', 'headphone', 'lpcm', 'recording level'],
  },
  {
    name: 'USB & Streaming',
    icon: '📺',
    specs: [],
    keywords: ['usb streaming', 'webcam', 'live stream', 'usb video', 'video data format', 'video resolution'],
  },
  {
    name: 'Accessories & Box Contents',
    icon: '📦',
    specs: [],
    keywords: ['supplied', 'accessories', 'in the box', 'included', 'what\'s in'],
  },
  {
    name: 'Accessibility',
    icon: '♿',
    specs: [],
    keywords: ['accessibility', 'screenreader', 'voice', 'assist'],
  },
];

export const OTHER_CATEGORY_NAME = 'Other';

export function formatSpecLabel(rawKey: string): string {
  if (!rawKey) return '';
  let key = String(rawKey).trim();
  if (!key) return '';

  key = key.replace(/[._]+/g, ' ').replace(/\s+/g, ' ').trim();

  const isMostlyAllCaps = /^[A-Z0-9\s()\-+/]+$/.test(key);
  if (isMostlyAllCaps) {
    // Handle known Sony joined labels that frequently appear from scraped specs.
    const joinedReplacements: Array<[RegExp, string]> = [
      [/MOVIERECORDINGSYSTEM/g, 'MOVIE RECORDING SYSTEM'],
      [/MOVIERECORDINGFORMAT/g, 'MOVIE RECORDING FORMAT'],
      [/RECORDINGSYSTEM/g, 'RECORDING SYSTEM'],
      [/RECORDINGFORMAT/g, 'RECORDING FORMAT'],
      [/EXPOSUREMODES/g, 'EXPOSURE MODES'],
      [/SHUTTERSPEED/g, 'SHUTTER SPEED'],
      [/SHUTTERAWBLOCK/g, 'SHUTTER AWB LOCK'],
      [/FLASHSYNCSPEED/g, 'FLASH SYNC SPEED'],
      [/XAVCSI/g, 'XAVC S-I'],
      [/XAVCHS/g, 'XAVC HS'],
      [/XAVCS/g, 'XAVC S'],
    ];
    for (const [pattern, replacement] of joinedReplacements) {
      key = key.replace(pattern, replacement);
    }

    key = key
      .replace(/([A-Z])(\d)/g, '$1 $2')
      .replace(/(\d)([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();

    return key;
  }

  return key
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (str) => str.toUpperCase());
}

export function formatSpecValue(specKey: string, rawValue: string): string {
  const key = (specKey || '').toLowerCase();
  const value = String(rawValue || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Handle legacy truncated aspect ratios like "3 :" caused by old normalization.
  // Keys arrive in several shapes ("aspect ratio", "aspectRatio", "aspect_ratio"),
  // so compare against a form stripped of separators.
  if (key.replace(/[^a-z0-9]/g, '').includes('aspectratio')) {
    const m = value.match(/^(\d+)\s*:\s*$/);
    if (m) {
      const lhs = m[1];
      const commonDenominator: Record<string, string> = {
        '1': '1',
        '3': '2',
        '4': '3',
        '16': '9',
        '17': '9',
      };
      const rhs = commonDenominator[lhs];
      if (rhs) return `${lhs}:${rhs}`;
    }
  }

  return value;
}

/** Flatten nested or list-slim spec maps into display key/value pairs. */
export function flattenSpecifications(
  specifications: Record<string, unknown> | string | null | undefined
): Record<string, string> {
  if (!specifications || typeof specifications !== 'object') return {};
  const out: Record<string, string> = {};

  const addLeaf = (key: string, value: unknown) => {
    if (!key || key.startsWith('_')) return;
    const stringValue = Array.isArray(value) ? value.join(', ') : value == null ? '' : String(value);
    if (!stringValue.trim() || stringValue === '[object Object]') return;
    out[key] = stringValue.trim();
  };

  const walk = (obj: unknown, prefix = '') => {
    if (obj == null) return;
    if (typeof obj !== 'object' || Array.isArray(obj)) {
      if (prefix) addLeaf(prefix, obj);
      return;
    }
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (!k || k.startsWith('_') || k === 'raw') continue;
      const key = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === 'object' && !Array.isArray(v)) walk(v, key);
      else addLeaf(key, v);
    }
  };

  walk(specifications);
  if (Object.keys(out).length === 0) {
    const raw = (specifications as { raw?: unknown }).raw;
    if (raw && typeof raw === 'object') walk(raw);
  }
  return out;
}

/** Non-empty, non-underscore-prefixed spec keys from a raw specifications map. */
export function validSpecKeys(specifications: Record<string, string> | string | null | undefined): string[] {
  return Object.keys(flattenSpecifications(specifications as Record<string, unknown> | undefined));
}

export function getSpecValue(
  specifications: Record<string, string> | string | null | undefined,
  specKey: string
): string | null {
  const value = flattenSpecifications(specifications as Record<string, unknown> | undefined)[specKey];
  return value?.trim() ? value : null;
}

/**
 * Groups spec keys into named categories: exact-key match first (Canon-style
 * camelCase keys), then keyword match (Sony-style human-readable keys), then
 * whatever's left goes to "Other". Same two-pass algorithm the public
 * SpecificationsViewer uses, so a key categorized as "Other" here is exactly
 * the set the "unmapped key" spec-issue heuristic should look at.
 */
export function categorizeSpecKeys(allSpecKeys: string[]): Map<string, string[]> {
  const categorizedSpecs = new Map<string, string[]>();
  const matchedKeys = new Set<string>();

  const matchesByKeyword = (specKey: string, keywords: string[]): boolean => {
    const keyLower = specKey.toLowerCase();
    return keywords.some((keyword) => keyLower.includes(keyword.toLowerCase()));
  };

  // First pass: exact key matching (for Canon-style camelCase keys)
  SPEC_CATEGORIES.forEach((category) => {
    const matchedSpecs: string[] = [];
    category.specs.forEach((specKey) => {
      if (allSpecKeys.includes(specKey)) {
        matchedSpecs.push(specKey);
        matchedKeys.add(specKey);
      }
    });
    if (matchedSpecs.length > 0) {
      categorizedSpecs.set(category.name, matchedSpecs);
    }
  });

  // Second pass: keyword matching for remaining specs (for Sony-style human-readable names)
  const stillUnmatched = allSpecKeys.filter((key) => !matchedKeys.has(key));
  stillUnmatched.forEach((specKey) => {
    for (const category of SPEC_CATEGORIES) {
      if (category.keywords && matchesByKeyword(specKey, category.keywords)) {
        const existing = categorizedSpecs.get(category.name) || [];
        existing.push(specKey);
        categorizedSpecs.set(category.name, existing);
        matchedKeys.add(specKey);
        break;
      }
    }
  });

  // Add still-unmatched specs to "Other" category
  const unmatchedSpecs = allSpecKeys.filter((key) => !matchedKeys.has(key));
  if (unmatchedSpecs.length > 0) {
    categorizedSpecs.set(OTHER_CATEGORY_NAME, unmatchedSpecs);
  }

  return categorizedSpecs;
}
